import useNeveForm from 'components/forms/NeveForm';
import TextAreaField from 'components/forms/TextAreaField';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import useModifyRequest from 'hooks/useModifyRequest';
import useUploadRequest from 'hooks/useUploadRequest';
import { Activity, ActivityType } from 'models/Activity';
import { Course } from 'models/Course';
import { FileDocumentType } from 'models/FileDocument';
import * as React from 'react';
import { createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import strings from 'strings/strings.json';
import DeleteActivityAlert from '../editActivityForms/shared/DeleteActivityAlert';
import BaseDetailsForm from '../shared/DetailsForm';
import { EditActivityFormCard } from '../shared/EditActivityFormCard';
import { AddImageComponent } from '../../AddImagesComponent';
import { EditPresentationSlide, PresentationSlideForm, PresentationSlideImage } from './PresentationSlide';

type EditPresentationSlideFormProps = {
    slide: Activity;
    returnToCourseStructure: () => void;
};

export default function EditPresentationSlideForm({
    slide: slideProp,
    returnToCourseStructure,
}: EditPresentationSlideFormProps): JSX.Element {
    const { setCourseState, editMode, refetchCourse, setEditActivityId } = React.useContext(BuildCourseContext);

    const [error, setError] = React.useState<string>('');
    const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    const { upload: uploadImage, uploading: uploadingImage } = useUploadRequest();
    const { modifyData: updateSlide, loading: updatingSlide } = useModifyRequest<EditPresentationSlide, boolean>(
        `activities/slide/${slideProp.id}`,
        'PUT',
    );

    React.useEffect(() => {
        return () => {
            setEditActivityId('');
        };
    }, []); //eslint-disable-line

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isDirty },
        reset,
    } = useNeveForm<PresentationSlideForm>({
        title: slideProp.title ?? '',
        content: slideProp.content ?? '',
        image: slideProp.imageDocuments.length
            ? {
                  type: 'existing',
                  id: slideProp.imageDocuments[0].id,
                  fileName: slideProp.imageDocuments[0].filename,
                  filePath: slideProp.imageDocuments[0].s3Filepath,
              }
            : null,
    });

    const { content, image } = watch();

    React.useEffect(() => {
        if (isDirty) setSaved(false);
    }, [isDirty]);

    async function saveSlide(formData: PresentationSlideForm) {
        setError('');

        if (!isDirty) {
            setSaved(true);
            return;
        }

        if (!(formData.content?.trim() ?? '') && !formData.image) {
            return setError(strings.presentationSlide.errorRequired);
        }

        let slideImage: PresentationSlideImage | undefined = undefined;

        if (formData.image?.type === 'new' && formData.image.file) {
            const { errors, value } = await uploadImage(formData.image.file);
            if (errors?.length || !value) {
                setError(strings.presentationSlide.errorCreate);
                return;
            }
            slideImage = {
                type: 'new',
                fileName: value.filename,
                filePath: value.s3Key,
                imageDescription: formData.image.imageDescription,
            };
        } else if (formData.image?.type === 'existing') {
            slideImage = {
                type: 'existing',
                imageId: formData.image.id,
                fileName: formData.image.fileName,
                filePath: formData.image.filePath,
                imageDescription: '',
            };
        }

        if (!editMode) {
            const request: EditPresentationSlide = {
                title: formData.title,
                content: formData.content?.trim() ?? '',
                image: slideImage,
            };
            const { errors } = await updateSlide(request);
            if (errors?.length) {
                setError(strings.presentationSlide.errorUpdate);
                return;
            }
            await refetchCourse();
        } else {
            setCourseState((prev) => {
                if (!prev) return prev;
                const copy = structuredClone(prev) as Course;
                const moduleIndex = copy.modules.findIndex((m) => m.activities.some((a) => a.id === slideProp.id));
                if (moduleIndex === -1) return prev;
                const module = copy.modules[moduleIndex];
                const activityIndex = module.activities.findIndex((a) => a.id === slideProp.id);
                if (activityIndex === -1) return prev;

                const updatedSlide = module.activities[activityIndex];
                updatedSlide.title = formData.title;
                updatedSlide.content = formData.content?.trim() ?? '';
                updatedSlide.imageDocuments = slideImage
                    ? [
                          {
                              id: slideImage.type === 'new' ? '' : slideImage.imageId,
                              filename: slideImage.fileName,
                              s3Filepath: slideImage.filePath,
                              documentType: FileDocumentType.IMAGE,
                              dateUpdated: new Date(),
                          },
                      ]
                    : [];

                module.activities = [
                    ...module.activities.slice(0, activityIndex),
                    updatedSlide,
                    ...module.activities.slice(activityIndex + 1),
                ];
                copy.modules[moduleIndex] = { ...module };
                return copy;
            });
        }

        reset({
            title: formData.title,
            content: formData.content,
            image: formData.image ?? null,
        });
        setSaved(true);
    }

    const contentMaxLength = 2000;

    return (
        <>
            <EditActivityFormCard
                title={strings.presentationSlide.titleEdit}
                onSubmit={handleSubmit(saveSlide)}
                pageOne={
                    <>
                        <BaseDetailsForm
                            register={register}
                            errors={errors}
                            hideDuration
                            hideShortDescription
                            autoFocus
                        />
                        <TextAreaField
                            maxLength={contentMaxLength}
                            charactersLeft={contentMaxLength - (content?.length ?? 0)}
                            fieldName={strings.taskContentForm.contentLabel}
                            aria={strings.taskContentForm.contentAria}
                            errorMessage={errors.content?.message}
                            min-height={500}
                            {...register('content', {
                                maxLength: {
                                    value: contentMaxLength,
                                    message: createErrorMessage(
                                        strings.taskContentForm.contentLabel,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />
                    </>
                }
                pageTwo={
                    <AddImageComponent
                        image={image}
                        setImage={(img) => setValue('image', img, { shouldDirty: true })}
                        disableButtons={uploadingImage || updatingSlide}
                    />
                }
                error={error}
                showSavedSuccessfully={saved && !editMode}
                showSavedSuccessfullyEditMode={saved && editMode}
                setDeleteAlertOpen={() => setDeleteAlertOpen(true)}
                disableDeleteTaskButton={uploadingImage || updatingSlide}
                disableSubmitButton={uploadingImage || updatingSlide || (editMode && !isDirty)}
                submitting={uploadingImage || updatingSlide}
                activityType={ActivityType.PRESENTATION_SLIDE}
                showLastActivityInModuleWarning={false}
            />
            {deleteAlertOpen && (
                <DeleteActivityAlert
                    taskId={slideProp.id}
                    moduleId={slideProp.moduleId}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={setDeleteAlertOpen}
                />
            )}
        </>
    );
}
